import { Link } from "@/lib/router-compat";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { INDUSTRIES } from "@/lib/industries";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" as const },
  }),
};

const IndustryGrid = () => {
  return (
    <section className="py-20 md:py-28 border-t border-border/50 bg-secondary/20">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Built for{" "}
            <span className="text-gradient">Your Industry</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Industry-specific proposal templates, pricing models, and contract language.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {INDUSTRIES.map((ind, i) => (
            <motion.div
              key={ind.key}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-30px" }}
              variants={fadeUp}
            >
              <Link to={ind.href}>
                <Card className="border-border bg-card shadow-card h-full hover:border-primary/30 transition-all duration-300 group cursor-pointer">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`h-10 w-10 rounded-lg ${ind.bgColor} flex items-center justify-center shrink-0`}>
                        <ind.icon className={`h-5 w-5 ${ind.color}`} />
                      </div>
                      {ind.status === "live" && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success/20 text-success">Live</span>
                      )}
                      {ind.status === "development" && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-info/20 text-info">In Development</span>
                      )}
                      {ind.status === "coming" && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-muted-foreground/20 text-muted-foreground">Coming Soon</span>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">{ind.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-primary opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      {ind.status === "live" ? "Get Started" : "Learn More"}
                      <ArrowRight className="h-3 w-3" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustryGrid;
